import { EMPTY_STRING } from '../../NonReact/constants/Common'
import { APIElements, getBaseServletPath } from '../../NonReact/constants/ApiConstants'
import { SERVLET_COMMAND, ServletCmds } from '../../NonReact/constants/ServletCommands'
import { isStringNotEmpty } from '../../NonReact/utils/GlobalFunctions'

/** Legacy list filter: selected CM Environment ID (GET/POST). Matches servlet base constant. */
export const LEGACY_ADMIN_ENTITY_LIST_CM_ENVIRONMENT_PARAM = 'cbCMEnvironmentID'

/** Legacy list filter: selected telephony server ID (GET/POST). Matches servlet base constant. */
export const LEGACY_ADMIN_ENTITY_LIST_TELSYS_PARAM = 'cbTelSysID'

/** Legacy list filter: selected partition GUID (GET/POST). Matches servlet base constant. */
export const LEGACY_ADMIN_ENTITY_LIST_PARTITION_PARAM = 'cbPartitionID'

/**
 * Legacy list filter: recursive CME / TelSys scope checkbox (GET/POST). Matches servlet base
 * constant.
 */
export const LEGACY_ADMIN_ENTITY_LIST_RECURSIVE_CME_TELSYS_PARAM = 'cbxRecursiveCMEnvironmentTelSyssSearch'

/**
 * Builds an absolute URL to the classic Admin Devices list with CME / TelSys filters applied.
 * Used when deep-linking from v2 surfaces (e.g. Telephony Servers extension counts) where the
 * target is devices, not v2 {@code /code}.
 *
 * @param brandingUrlMapping - Branding app folder (e.g. from {@link Branding.URLMapping}); leading
 *   or trailing slashes are stripped. When empty, {@link APIElements.AKIXI_2019} is used.
 * @param cmeId - CM Environment ID to select.
 * @param telSysId - Telephony server ID to select.
 * @param includeRecursiveCmeTelSysSearch - Whether child CMEs are included when resolving TelSys
 *   scope (matches the v2 include-CME-children switch intent).
 * @param partitionId - Optional partition GUID; when provided, the legacy partition filter is set.
 * @returns Full URL (including origin) suitable for a plain {@code <a href>}.
 */
export function buildLegacyAdminDevicesListHref(
    brandingUrlMapping: Definable<string>,
    cmeId: string,
    telSysId: number,
    includeRecursiveCmeTelSysSearch: boolean,
    partitionId?: string
): string {
    const trimmedMapping: string = (brandingUrlMapping ?? EMPTY_STRING).replace(/\//g, EMPTY_STRING)
    const appFolder: string = isStringNotEmpty(trimmedMapping) ? trimmedMapping : APIElements.AKIXI_2019

    return getBaseServletPath(appFolder)
        .appendQueryParams({
            [SERVLET_COMMAND]: ServletCmds.ADMIN_DEVICES,
            [LEGACY_ADMIN_ENTITY_LIST_CM_ENVIRONMENT_PARAM]: cmeId,
            [LEGACY_ADMIN_ENTITY_LIST_TELSYS_PARAM]: String(telSysId),
            ...(isStringNotEmpty(partitionId ?? EMPTY_STRING)
                ? { [LEGACY_ADMIN_ENTITY_LIST_PARTITION_PARAM]: partitionId as string }
                : {}),
            [LEGACY_ADMIN_ENTITY_LIST_RECURSIVE_CME_TELSYS_PARAM]: includeRecursiveCmeTelSysSearch ? 'true' : 'false'
        })
        .constructURL()
}

/**
 * Builds an absolute URL to the classic Admin Users list with CME / TelSys filters applied.
 * Used when deep-linking from v2 surfaces before the routed users list is available on the branch.
 *
 * @param brandingUrlMapping - Branding app folder (e.g. from {@link Branding.URLMapping}); leading
 *   or trailing slashes are stripped. When empty, {@link APIElements.AKIXI_2019} is used.
 * @param cmeId - CM Environment ID to select.
 * @param telSysId - Telephony server ID to select.
 * @param includeRecursiveCmeTelSysSearch - Whether child CMEs are included when resolving TelSys
 *   scope (matches the v2 include-CME-children switch intent).
 * @param partitionId - Optional partition GUID; when provided, the legacy partition filter is set.
 * @returns Full URL (including origin) suitable for a plain {@code <a href>}.
 */
export function buildLegacyAdminUsersListHref(
    brandingUrlMapping: Definable<string>,
    cmeId: string,
    telSysId: number,
    includeRecursiveCmeTelSysSearch: boolean,
    partitionId?: string
): string {
    const trimmedMapping: string = (brandingUrlMapping ?? EMPTY_STRING).replace(/\//g, EMPTY_STRING)
    const appFolder: string = isStringNotEmpty(trimmedMapping) ? trimmedMapping : APIElements.AKIXI_2019

    return getBaseServletPath(appFolder)
        .appendQueryParams({
            [SERVLET_COMMAND]: ServletCmds.ADMIN_USERS,
            [LEGACY_ADMIN_ENTITY_LIST_CM_ENVIRONMENT_PARAM]: cmeId,
            [LEGACY_ADMIN_ENTITY_LIST_TELSYS_PARAM]: String(telSysId),
            ...(isStringNotEmpty(partitionId ?? EMPTY_STRING)
                ? { [LEGACY_ADMIN_ENTITY_LIST_PARTITION_PARAM]: partitionId as string }
                : {}),
            [LEGACY_ADMIN_ENTITY_LIST_RECURSIVE_CME_TELSYS_PARAM]: includeRecursiveCmeTelSysSearch ? 'true' : 'false'
        })
        .constructURL()
}
